"use client";

import React from "react";
import Link from "next/link";
import { siteConfig } from "@/config/site.config";

export const LoQueVieneSection: React.FC = () => {
  const items = [
    {
      id: "canal",
      numero: "I",
      ...siteConfig.loQueViene.canal,
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
    },
    {
      id: "audiolibros",
      numero: "II",
      ...siteConfig.loQueViene.audiolibros,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .513v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
    },
    {
      id: "podcast",
      numero: "III",
      ...siteConfig.loQueViene.podcast,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
        </svg>
      ),
    },
    {
      id: "ensayos",
      numero: "IV",
      ...siteConfig.loQueViene.ensayos,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
        </svg>
      ),
    },
  ];

  return (
    <section className="relative py-24 md:py-32 px-6 md:px-12 bg-[#030508] border-t border-gold/10 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(197,160,89,0.05),transparent_60%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center flex flex-col items-center mb-16">
          <div className="flex items-center gap-2 mb-4">
            <span className="flex w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-[10px] font-semibold tracking-[0.4em] text-gold uppercase">
              Horizontes en Construcción
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-text-pure tracking-wide font-light">
            Lo que <span className="font-playfair italic text-gold">Viene</span>
          </h2>
          <div className="w-12 h-[1px] bg-gold/50 mt-6 mb-6" />
          <p className="text-text-muted text-sm md:text-base font-light max-w-2xl leading-relaxed">
            Nuestros proyectos centrales de formación y divulgación filosófica, organizados paso a paso sin prisas ni atajos comerciales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item) => (
            <Link
              key={item.id}
              href={item.enlace}
              className="relative flex flex-col p-8 rounded-xl border border-gold/15 bg-bg-secondary/30 hover:border-gold/45 hover:-translate-y-1 transition-all duration-500 group shadow-lg shadow-black/30"
            >
              {/* Insignia Próximamente */}
              <div className="flex items-center justify-between mb-6">
                <span className="font-serif text-xs text-gold/60 font-light tracking-widest">
                  {item.numero}
                </span>
                <span className="text-[8px] font-mono tracking-widest uppercase text-gold border border-gold/30 bg-gold/10 px-2 py-0.5 rounded-sm">
                  Próximamente
                </span>
              </div>

              {/* Icono */}
              <div className="w-12 h-12 rounded-lg border border-gold/20 bg-gold/5 flex items-center justify-center text-gold mb-6 group-hover:scale-105 transition-transform">
                {item.icon}
              </div>

              {/* Título */}
              <h3 className="font-serif text-xl text-text-pure font-light mb-3 group-hover:text-gold-light transition-colors">
                {item.titulo}
              </h3>

              {/* Descripción breve (1 línea o 2) */}
              <p className="text-text-muted text-xs leading-relaxed font-light mb-6 flex-grow">
                {item.descripcion}
              </p>

              {/* Enlace */}
              <div className="flex items-center gap-2 text-[10px] text-gold tracking-widest uppercase pt-4 border-t border-gold/10 font-medium">
                <span>Ver detalles</span>
                <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
